// п. 20–21 (ФЗ-152, РГ-01/РГ-02/РГ-08). Страница /activation: активация
// приглашения в пять шагов по макету «Активация».
//
//   1. почта → код письмом (ответ один и тот же при любом 2xx — РГ-01);
//   2. код из 6 цифр; повтор — по таймеру;
//   3. ФИО и телефон (только после подтверждения кода — РГ-03);
//   4. текст согласия с редакцией и отметкой;
//   5. пароль и повтор с проверкой длины и совпадения → complete.
//
// Запросы и их порядок не менялись: `requestCode`, `verify`, `consent`,
// `complete`. В `complete` уходит версия согласия, **показанная человеку**
// (РГ-02), — её не перечитывают к моменту отправки.

import React, { FC, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { Context } from "../../index";
import ActivationService, {
  ActivationConsent,
} from "../../services/ActivationService";
// `E.8`: код активации — шесть ячеек.
import CodeCells from "./CodeCells";
import { landingForRole } from "../../utils/returnPath";
import {
  ACTIVATION_CODE_FAILED_FALLBACK,
  ACTIVATION_CODE_HINT,
  ACTIVATION_CODE_LENGTH,
  ACTIVATION_CONSENT_REQUIRED_MESSAGE,
  ACTIVATION_COOLDOWN_SECONDS,
  ACTIVATION_PASSWORD_MIN_LENGTH,
  ACTIVATION_PASSWORD_MISMATCH_MESSAGE,
  ACTIVATION_PASSWORD_RULES_MESSAGE,
  ACTIVATION_PERSONAL_REQUIRED_MESSAGE,
  ACTIVATION_REQUEST_ANSWER,
  rateLimitedMessage,
  retryAfterSeconds,
} from "../../utils/activation";
import "./ActivationPage.css";

/** Текст отказа: сервер называет причину, свой — только запасной. */
function messageOf(error: any, fallback: string): string {
  return error?.response?.data?.message || fallback;
}

type Step = 1 | 2 | 3 | 4 | 5;

const STEP_TITLES: Record<Step, string> = {
  1: "Добро пожаловать в личный кабинет",
  2: "Код из письма",
  3: "Как к вам обращаться",
  4: "Согласие на обработку данных",
  5: "Пароль для входа",
};

const ActivationPage: FC = () => {
  const { store } = useContext(Context);
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(1);
  const [email, setEmail] = useState<string>("");
  const [answer, setAnswer] = useState<string | null>(null);
  // `21.1`: подсказка под полем кода после любого отказа `verify`.
  const [codeFailed, setCodeFailed] = useState(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [cooldown, setCooldown] = useState<number>(0);

  const [code, setCode] = useState<string>("");
  const [confirmationToken, setConfirmationToken] = useState<string | null>(null);
  const [consent, setConsent] = useState<ActivationConsent | null>(null);
  const [consentChecked, setConsentChecked] = useState<boolean>(false);

  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [surName, setSurName] = useState<string>("");
  const [tel, setTel] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [repeatPassword, setRepeatPassword] = useState<string>("");

  useEffect(() => {
    if (cooldown <= 0) return undefined;

    const timer = setTimeout(() => setCooldown((value) => value - 1), 1000);

    return () => clearTimeout(timer);
  }, [cooldown]);

  const rateLimited = (wait = ACTIVATION_COOLDOWN_SECONDS) => {
    setAnswer(rateLimitedMessage(wait));
    setCooldown(wait);
  };

  const requestCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting || cooldown > 0) return;

    setSubmitting(true);

    try {
      await ActivationService.requestCode(email);
      setAnswer(ACTIVATION_REQUEST_ANSWER);
      setStep(2);
      setCooldown(ACTIVATION_COOLDOWN_SECONDS);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        // `20.1`: ждём столько, сколько назвал сервер; не назвал — своё обычное.
        rateLimited(retryAfterSeconds(error) ?? ACTIVATION_COOLDOWN_SECONDS);
      } else {
        // Сбой связи текст не меняет: иначе разница в ответах снова стала бы
        // способом отличить внесённый адрес от прочего.
        setAnswer(ACTIVATION_REQUEST_ANSWER);
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** Показ согласия: версию запоминаем — её же отправим на шаге 5 (`РГ-02`). */
  const loadConsent = async () => {
    setSubmitting(true);

    try {
      const shown = await ActivationService.consent();
      setConsent(shown);
      setAnswer(null);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        rateLimited();
      } else {
        setAnswer(
          messageOf(error, "не удалось получить текст согласия — повторите"),
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting || !code.trim()) return;

    setSubmitting(true);

    try {
      const result = await ActivationService.verify(email, code);
      setConfirmationToken(result.confirmationToken);
      setAnswer(null);
      setCodeFailed(false);
      await loadConsent();
      // Даже если текст согласия не загрузился, код уже подтверждён: шаг 4
      // предложит показать текст ещё раз, а не отправит человека назад.
      setStep(3);
    } catch (error: any) {
      // `21.1`: подсказка — при любом отказе, включая предел частоты.
      setCodeFailed(true);

      if (error?.response?.status === 429) {
        rateLimited(retryAfterSeconds(error) ?? ACTIVATION_COOLDOWN_SECONDS);
      } else {
        // Сервер отвечает одним общим текстом — показываем его как есть.
        setAnswer(messageOf(error, ACTIVATION_CODE_FAILED_FALLBACK));
      }
      setSubmitting(false);
    }
  };

  /** Шаг 3 → 4: ФИО обязательно, телефон — нет. */
  const goToConsent = (e: React.FormEvent) => {
    e.preventDefault();

    if (!firstName.trim() || !lastName.trim()) {
      setAnswer(ACTIVATION_PERSONAL_REQUIRED_MESSAGE);
      return;
    }

    setAnswer(null);
    setStep(4);
  };

  /** Шаг 4 → 5: без показанного текста и отметки дальше нельзя. */
  const goToPassword = (e: React.FormEvent) => {
    e.preventDefault();

    if (!consent) return;

    if (!consentChecked) {
      setAnswer(ACTIVATION_CONSENT_REQUIRED_MESSAGE);
      return;
    }

    setAnswer(null);
    setStep(5);
  };

  const finishActivation = async (e: React.FormEvent) => {
    e.preventDefault();

    if (submitting || !consent || !confirmationToken) return;

    if (!consentChecked) {
      setAnswer(ACTIVATION_CONSENT_REQUIRED_MESSAGE);
      return;
    }
    if (!firstName.trim() || !lastName.trim()) {
      setAnswer(ACTIVATION_PERSONAL_REQUIRED_MESSAGE);
      return;
    }
    if (password.length < ACTIVATION_PASSWORD_MIN_LENGTH) {
      setAnswer(ACTIVATION_PASSWORD_RULES_MESSAGE);
      return;
    }
    if (password !== repeatPassword) {
      setAnswer(ACTIVATION_PASSWORD_MISMATCH_MESSAGE);
      return;
    }

    setSubmitting(true);

    try {
      await ActivationService.complete({
        email,
        confirmationToken,
        password,
        firstName,
        lastName,
        surName: surName || null,
        tel: tel || null,
        consentAccepted: true,
        // РГ-02: именно показанная версия, а не действующая на момент отправки.
        consentVersion: consent.version,
      });

      await store.login(email, password);

      if (store.isAuth) {
        navigate(landingForRole(store.user?.role));
        return;
      }

      setAnswer(store.authError || ACTIVATION_CODE_FAILED_FALLBACK);
    } catch (error: any) {
      if (error?.response?.status === 429) {
        rateLimited();
      } else {
        setAnswer(messageOf(error, ACTIVATION_CODE_FAILED_FALLBACK));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const requestButtonTitle =
    cooldown > 0 ? `Повторить через ${cooldown} с` : "Получить код";
  const resendButtonTitle =
    cooldown > 0 ? `Повторить через ${cooldown} с` : "Отправить ещё раз";

  return (
    <div className="activation" data-form="activation">
      <div className="activation-card">
        <div className="activation-progress">
          <span className="activation-progress__step">Шаг {step} из 5</span>
          <div className="activation-progress__bar" aria-hidden="true">
            {([1, 2, 3, 4, 5] as Step[]).map((number) => (
              <span
                key={number}
                className={
                  number <= step
                    ? "activation-progress__bar-fill"
                    : "activation-progress__bar-rest"
                }
              />
            ))}
          </div>
        </div>

        <h1 className="activation-title">{STEP_TITLES[step]}</h1>

        {step === 1 ? (
          <>
            <p className="activation-text">
              Вас пригласили по договору на машиноместо. Подтвердим почту —
              пришлём на неё код.
            </p>

            <form onSubmit={requestCode} data-step="code-request">
              <label className="activation-field" htmlFor="activation-email">
                <span>адрес почты:</span>
                <input
                  className="activation-input"
                  id="activation-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoCapitalize="none"
                  autoComplete="email"
                />
              </label>

              <button
                type="submit"
                className="activation-btn activation-btn--primary"
                disabled={submitting || cooldown > 0}
              >
                {requestButtonTitle}
              </button>
            </form>

            <p className="activation-text activation-text--small">
              Не ваш адрес? Обратитесь в поддержку.
            </p>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="activation-text">
              Отправили {ACTIVATION_CODE_LENGTH} цифр на {email}.
            </p>

            <form onSubmit={verifyCode} data-step="code-verify">
              <div className="activation-field">
                <label htmlFor="activation-code">код из письма:</label>
                {/* `E.8`: шесть ячеек вместо одного поля — вставка кода из письма
                    заполняет все, буквы не принимаются. */}
                <CodeCells
                  id="activation-code"
                  value={code}
                  length={ACTIVATION_CODE_LENGTH}
                  onChange={setCode}
                />
              </div>

              {codeFailed ? (
                <div className="activation-hint" role="status">
                  {ACTIVATION_CODE_HINT}
                </div>
              ) : null}

              <button
                type="submit"
                className="activation-btn activation-btn--primary"
                disabled={submitting}
              >
                Подтвердить код
              </button>
            </form>

            <form onSubmit={requestCode} data-step="code-resend">
              <p className="activation-text activation-text--small">
                {cooldown > 0
                  ? `Отправить ещё раз через ${cooldown} с`
                  : "Код не пришёл? Отправьте ещё раз."}
              </p>
              <button
                type="submit"
                className="activation-btn"
                disabled={submitting || cooldown > 0}
              >
                {resendButtonTitle}
              </button>
            </form>
          </>
        ) : null}

        {step === 3 ? (
          <form onSubmit={goToConsent} data-step="personal">
            <label className="activation-field" htmlFor="activation-last-name">
              <span>фамилия:</span>
              <input
                className="activation-input"
                id="activation-last-name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
              />
            </label>
            <label className="activation-field" htmlFor="activation-first-name">
              <span>имя:</span>
              <input
                className="activation-input"
                id="activation-first-name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoComplete="given-name"
              />
            </label>
            <label className="activation-field" htmlFor="activation-sur-name">
              <span>отчество — если есть:</span>
              <input
                className="activation-input"
                id="activation-sur-name"
                value={surName}
                onChange={(e) => setSurName(e.target.value)}
                autoComplete="additional-name"
              />
            </label>
            <label className="activation-field" htmlFor="activation-tel">
              <span>телефон:</span>
              <input
                className="activation-input"
                id="activation-tel"
                value={tel}
                onChange={(e) => setTel(e.target.value)}
                inputMode="tel"
                autoComplete="tel"
              />
            </label>

            <div className="activation-actions">
              <button
                type="button"
                className="activation-btn"
                onClick={() => setStep(2)}
              >
                Назад
              </button>
              <button
                type="submit"
                className="activation-btn activation-btn--primary"
              >
                Далее
              </button>
            </div>
          </form>
        ) : null}

        {step === 4 ? (
          <form onSubmit={goToPassword} data-step="consent">
            {consent ? (
              <div
                className="activation-consent"
                data-activation-consent
                data-consent-version={consent.version}
              >
                <b className="activation-consent__version">
                  Редакция {consent.version}
                </b>
                <div className="activation-consent__text">{consent.text}</div>
              </div>
            ) : (
              <div className="activation-consent activation-consent--empty">
                <p className="activation-text">
                  Текст согласия не загрузился. Покажите его ещё раз, чтобы
                  прочитать перед отметкой.
                </p>
                <button
                  type="button"
                  className="activation-btn"
                  disabled={submitting}
                  onClick={loadConsent}
                >
                  Показать текст согласия
                </button>
              </div>
            )}

            <label className="activation-check" htmlFor="activation-consent">
              <input
                id="activation-consent"
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
              />
              <span>
                согласен с текстом
                {consent ? `, версия ${consent.version}` : ""}
              </span>
            </label>

            <div className="activation-actions">
              <button
                type="button"
                className="activation-btn"
                onClick={() => setStep(3)}
              >
                Назад
              </button>
              <button
                type="submit"
                className="activation-btn activation-btn--primary"
                disabled={!consent || !consentChecked || submitting}
              >
                Принять и продолжить
              </button>
            </div>
          </form>
        ) : null}

        {step === 5 ? (
          <form onSubmit={finishActivation} data-step="activation-complete">
            <label className="activation-field" htmlFor="activation-password">
              <span>пароль:</span>
              <input
                className="activation-input"
                id="activation-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            <label
              className="activation-field"
              htmlFor="activation-password-repeat"
            >
              <span>повторите пароль:</span>
              <input
                className="activation-input"
                id="activation-password-repeat"
                type="password"
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                autoComplete="new-password"
              />
            </label>

            <ul className="activation-rules">
              <li
                className={
                  password.length >= ACTIVATION_PASSWORD_MIN_LENGTH
                    ? "activation-rules__ok"
                    : undefined
                }
              >
                не короче {ACTIVATION_PASSWORD_MIN_LENGTH} символов
              </li>
              <li
                className={
                  password && password === repeatPassword
                    ? "activation-rules__ok"
                    : undefined
                }
              >
                пароли совпадают
              </li>
            </ul>

            <div className="activation-actions">
              <button
                type="button"
                className="activation-btn"
                onClick={() => setStep(4)}
              >
                Назад
              </button>
              <button
                type="submit"
                className="activation-btn activation-btn--primary"
                disabled={submitting}
              >
                Завершить
              </button>
            </div>
          </form>
        ) : null}

        {answer ? (
          <div
            className="activation-answer"
            role="status"
            data-activation-answer
          >
            {answer}
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default ActivationPage;
